import local from "../modules/local";
import SwipeReveal from "./swipe-reveal";

// the selector to instantiate the overlay on
const EL_SELECTOR = ".overlay--portrait-reveal";

// the local storage key to store the portrait reveal items in
const LOCAL_STORAGE_KEY = "direct-positive-portrait-reveal";

class PortraitRevealOverlay {
    constructor(el) {
        // store the element for quick access
        this.el = el;

        // will store the porttrait set in "setPortraitFromQuery"
        this.portrait = null;

        this.swipeReveal = null;
        // store intro messages
        this.messages = [
            ...document.querySelectorAll(".page-overlay__message"),
        ];
        this.currentMessage = 0;

        this.messageInterval = setInterval(() => {
            this.updateShownMessage();
        }, 5000);

        this.dismissButton = document.querySelector(".page-overlay__dismiss");

        // instantiate the plugin
        this.initInstance();
    }

    /**
     * Toggle the page element's styles to prevent scrolling when overlay is active
     */
    togglePageStyles() {
        // get the element
        const page = document.getElementById("page");

        if (page.hasAttribute("style")) {
            // if the style attribute is present, remove it
            page.removeAttribute("style");
        } else {
            // if the style attribute is not present, add it
            // match the height of the overlay
            page.style.setProperty("height", this.el.offsetHeight + "px");
            // enure there is nothing flowing outside
            page.style.setProperty("overflow", "hidden");
            // ensure we cannot act on the page element
            // page.style.setProperty("pointer-events", "none");
        }
    }

    /**
     * Fetches entries to dynamically place content in the overlay
     * @returns {Promise<boolean>}
     */
    async fetchData() {
        try {
            // build the url to get the data from
            const url = `${this.constructor.theme}/data/portrait-reveal.json`;

            // fetch the data
            const response = await fetch(url);

            // parse the data
            const data = await response.json();

            // ensure we only have valid entries
            const filtered = data.filter((item) => item.portrait_reveal_image);

            return Promise.resolve(filtered);
        } catch (error) {
            return Promise.reject(error);
        }
    }

    /**
     * Set the portrait image based on the query string or a random one
     * @returns {Promise<boolean>}
     */
    async setPortrait() {
        try {
            // get the data
            const data = await this.fetchData();

            // get the query string object
            const params = new URLSearchParams(window.location.search);
            // get the portrait value
            const portrait = params.get("portrait");

            if (portrait) {
                // if we have a query string, set it
                this.setPortraitFromQuery(data, portrait);
            } else {
                // if we do not have a query string, set a random one
                this.setPortraitFromRandom(data);
            }

            // add the image
            this.setPortraitImage();

            // toggle the page styles
            this.togglePageStyles();

            return Promise.resolve(true);
        } catch (err) {
            return Promise.reject(err);
        }
    }

    /**
     * Set the portrait found in the given data based on a slug from the query string
     * @param {Array} data - the data to search through
     * @param {Boolean|String} portrait - the slug to search for
     */
    setPortraitFromQuery(data = [], portrait = false) {
        // find the item with the slug that matches the query string
        const target = data.find((item) => item.slug === portrait);

        if (!target) {
            // if not found, default to a random one
            this.setPortraitFromRandom(data);
        } else {
            // if found, set it
            this.portrait = target;
        }
    }

    /**
     * Set the portrait found in the given data based on a random index
     * @param {Array} data - the data to search through
     * */
    setPortraitFromRandom(data = []) {
        const randomIndex = Math.floor(Math.random() * data.length);
        this.portrait = data[randomIndex];
    }

    /**
     * Set the portrait image on the overlay
     */
    setPortraitImage() {
        // get the element to add the image to
        const targetEl = this.el.querySelector(".page-overlay__image");

        // create the image element
        const img = new Image();

        // set the source from portrait
        img.src = this.portrait.portrait_reveal_image;

        SwipeReveal.theme = this.constructor.theme;
        this.swipeReveal = new SwipeReveal(targetEl, img);
    }

    updateIntroMessage(message) {
        const targetEl = this.el.querySelector(".page-overlay__intro");
        targetEl.innerHTML = message;
    }

    updateShownMessage() {
        this.clearActiveMessages();
        this.messages[this.currentMessage].classList.add("active");
        this.currentMessage =
            this.currentMessage > this.messages.length - 2
                ? clearInterval(this.messageInterval)
                : this.currentMessage + 1;
    }

    clearActiveMessages() {
        this.messages.forEach((msg) => {
            msg.classList.remove("active");
        });
    }

    async initInstance() {
        try {
            await this.setPortrait();
            this.el.classList.add("active");
            if (Array.isArray(this.messages) && this.messages.length > 0) {
                this.clearActiveMessages();
                this.updateShownMessage();
            }
            this.dismissButton.addEventListener("click", () => {
                this.close();
            });
        } catch (error) {
            console.error(error);
        }
    }

    close() {
        this.togglePageStyles();
        this.el.classList.remove("active");
    }

    /**
     * Instantiates the plugin on the overlay element
     */
    static init() {
        // get the query string object
        const params = new URLSearchParams(window.location.search);
        // get the portrait value
        const portrait = params.get("portrait");

        const storedKey = localStorage.getItem(
            "direct-positive-portrait-reveal"
        );

        if (!storedKey || portrait) {
            localStorage.setItem("direct-positive-portrait-reveal", "x");
            const el = document.querySelector(`${EL_SELECTOR}`);
            return new PortraitRevealOverlay(el);
        }
    }
}

export default PortraitRevealOverlay;
